import React from "react";
import { Modal, Descriptions, Tag, Typography, Table } from "antd";
import moment from "moment";
import {
  ClockCircleOutlined,
} from "@ant-design/icons";
import "./ViewWithdrawalModal.css";

const { Text } = Typography;

function ViewWithdrawalModal({ open, onClose, data }) {
  if (!data) return null;

  const owner = data.owner || {};
  const bank = data.bank || {};

  // -----------------------------
  // STATUS COLOR MAP
  // -----------------------------
  const auditLogsStatusColor = {
    PENDING: "orange",
    UNDER_REVIEW: "blue",
    COMPLETED: "green",
    REJECT: "red",
    REJECTED: "red",
    CANCELLED: "volcano",
  };

  const statusColor =
    data.status === "PROCESSING"
      ? "gold"
      : data.status === "COMPLETED"
        ? "green"
        : data.status === "REJECTED"
          ? "red"
          : data.status === "CANCELLED"
            ? "volcano"
            : "blue";
  const rejectedLog = data?.audit_logs?.find(
    (log) => log.new_status === "REJECTED"
  );

  const rejectionReason = rejectedLog?.reason;

  const auditLogsColumns = [
    {
      title: "Admin ID",
      dataIndex: "admin_id",
      render: (text) => <Text code>{text ?? "N/A"}</Text>,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text) => <Tag color={auditLogsStatusColor[text]}>{text}</Tag>,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (text) => <Tag >{text}</Tag>,
    },
    {
      title: "Previous Status",
      dataIndex: "previous_status",
      render: (text) => <Tag color={auditLogsStatusColor[text]}>{text}</Tag>,
    },
    {
      title: "New Status",
      dataIndex: "new_status",
      render: (text) => <Tag color={auditLogsStatusColor[text]}>{text}</Tag>,
    },
    {
      title: "Time Stamp",
      dataIndex: "timestamp",
      render: (text) => <Text>{moment(text).format("DD MMM YYYY, hh:mm A")}</Text>,
    },

  ];


  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ maxWidth: 1050 }}
      bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
      className="withdrawal-view-modal"
      title={
        <div className="modal-header">
          <span className="modal-title">Withdrawal Request Details</span>
          <Tag className="status-pill" color={statusColor}>
            {data.status}
          </Tag>
        </div>
      }
    >
      {rejectionReason && (
        <span style={{ marginLeft: 12, color: "red", fontSize: 14, fontWeight: 600 }}>
          Rejected Reason: {rejectionReason || "N/A"}
        </span>
      )}
      {/* ================= USER DETAILS ================= */}
      <div className="section-card">
        <div className="section-title">User Details</div>
        <div className="section-inner">
          <Descriptions column={2} size="small">
            <Descriptions.Item label="User Type">
              <Tag className="pill">{owner.type || "N/A"}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="User ID">
              <Text code>{owner.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Full Name">
              {owner.name || (owner.first_name ?? "") + " " + (owner.middle_name ?? "") + " " + (owner.last_name ?? "") || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {owner.email || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Mobile">
              {owner.mobile || "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </div>
      </div>

      {/* ================= WITHDRAWAL DETAILS ================= */}
      <div className="section-card">
        <div className="section-title">Withdrawal Details</div>
        <div className="section-inner">
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Request ID">
              <Text code>{data.request_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Requested Amount">
              <Text strong className="amount-text">
                NZ$ {data.amount_requested}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Fees">
              NZ$ {data.fees}
            </Descriptions.Item>
            <Descriptions.Item label="Net Payable">
              <Text strong>NZ$ {data.net_payable}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Wallet Balance at Request">
              NZ$ {data.wallet_balance_at_request}
            </Descriptions.Item>
            <Descriptions.Item label="Withdrawal Method">
              {data?.withdrawal_method || "BANK TRANSFER"}
            </Descriptions.Item>
            <Descriptions.Item label="Requested At">
              <ClockCircleOutlined />{" "}
              {moment(data.created_at).format("DD MMM YYYY, hh:mm A")}
            </Descriptions.Item>
            {data.approved_at && (
              <Descriptions.Item label="Approved At">
                {moment(data.approved_at).format("DD MMM YYYY, hh:mm A")}
              </Descriptions.Item>
            )}
            {data.processed_at && (
              <Descriptions.Item label="Processed At">
                {moment(data.processed_at).format("DD MMM YYYY, hh:mm A")}
              </Descriptions.Item>
            )}
            {data.rejection_reason && (
              <Descriptions.Item label="Rejection Reason">
                {data.rejection_reason}
              </Descriptions.Item>
            )}
            {data.failed_reason && (
              <Descriptions.Item label="Failure Reason">
                {data.failed_reason}
              </Descriptions.Item>
            )}

          </Descriptions>
        </div>
      </div>

      {/* ================= BANK DETAILS ================= */}
      <div className="section-card">
        <div className="section-title">Bank Details</div>
        <div className="section-inner">
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Bank Name">
              {bank.bank_name || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Account Holder">
              {bank.account_holder || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Account Number">
              {bank.account_number || "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </div>
      </div>

      {/* ================= KYC DETAILS ================= */}
      <div className="section-card">
        <div className="section-title">KYC Details</div>

        <div className="section-inner">
          <Descriptions column={2} size="small">
            <Descriptions.Item label="KYC ID">
              {data?.kyc?.id || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="User ID">
              {data?.kyc?.user || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColor[data?.kyc?.status] || "default"}>
                {data?.kyc?.status || "N/A"}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Submitted At">
              {data?.kyc?.submitted_at
                ? new Date(data?.kyc?.submitted_at).toLocaleString()
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Approved At">
              {data?.kyc?.approved_at
                ? new Date(data?.kyc?.approved_at).toLocaleString()
                : "N/A"}
            </Descriptions.Item>

            <Descriptions.Item label="Expiry Date">
              {data?.kyc?.expiry_date
                ? new Date(data?.kyc?.expiry_date).toLocaleDateString()
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Rejection Reason">
              {data?.kyc?.rejection_reason || "N/A"}
            </Descriptions.Item>

            <Descriptions.Item label="Admin Comment">
              {data?.kyc?.admin_comment || "N/A"}
            </Descriptions.Item>
            {/* <Descriptions.Item label="Expiring Soon">
              {data?.kyc?.is_expiring_soon ? "Yes" : "No"}
            </Descriptions.Item> */}

          </Descriptions>
        </div>
      </div>

      {/* ================= AUDIT LOGS ================= */}
      {data?.audit_logs.length > 0 && <div className="section-card">
        <div className="section-title">Audit Logs</div>
        <div className="section-inner">
          <Table className="table-responsive" dataSource={data?.audit_logs} columns={auditLogsColumns} pagination={false} />
        </div>
      </div>}
    </Modal>
  );
}

export default ViewWithdrawalModal;
